/**
 * Owner Attestation screen — item 12 (Asana 1216373781992284), "capture layer only" (Ryan,
 * 2026-07-30, "Revisions to Portfolio screen" subtask).
 *
 * Jarvis owns the question set + answer schema; distribution (email / MS Form / Teams) and
 * collection happen OUTSIDE Jarvis, because these are client staff with no Jarvis logins. So this
 * screen has exactly three jobs, matching Ryan's own framing: track coverage ("EUC-0095 — 9 of 11
 * answered") and the gaps behind it, let a consultant enter answers directly (the same "answered"
 * state as an owner's response — no scoring distinction between the two, see attestationQuestions.js),
 * and move structured answers in and out of Jarvis (export a per-owner pack, import responses back).
 *
 * NOT built here, deliberately: a mailer/sender, and wiring an answered question into a live
 * disposition score (dispositionEngine.js's completeness_penalty already exists and each question
 * names the factor id it would feed — see attestationQuestions.js's `feeds` — but connecting that
 * is a scoring change, out of scope for "capture layer only").
 */

import { useMemo, useState } from 'react';
import { useAttestation } from './useAttestation';
import type { AttestationQuestion, CoverageEucRow } from './useAttestation';
import { useFrameworkData } from './useFrameworkData';
import { color, font } from '../tokens';

const C = {
  accent: color.accent, deep: color.navy, ink: '#1e1b3a',
  body: '#4a4966', subtle: color.textMuted, label: '#7c7a95', border: color.cardBorder, soft: '#f9fafb', surface: color.surface,
};
const mono = 'JetBrains Mono, monospace';

type Tab = 'gaps' | 'answer' | 'distribute' | 'config';

interface AnswerDraft { value: string | string[]; extra?: string | null; }

function ProgressBar({ pct }: { pct: number }) {
  return (
    <div style={{ height: 6, borderRadius: 999, background: '#f1f0f6', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.max(0, Math.min(100, pct))}%`, background: `linear-gradient(90deg, ${C.deep}, ${C.accent})`, transition: 'width 240ms ease' }} />
    </div>
  );
}

function ListEditor({ label, items, onChange }: { label: string; items: string[]; onChange: (items: string[]) => void }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const commit = () => { const v = draft.trim(); if (v && !items.includes(v)) onChange([...items, v]); setDraft(''); setAdding(false); };
  const remove = (v: string) => onChange(items.filter((x) => x !== v));
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, color: C.ink, marginBottom: 8 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {items.map((v) => (
          <span key={v} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontFamily: mono, color: C.body, background: C.soft, border: `1px solid ${C.border}`, borderRadius: 999, padding: '4px 8px 4px 10px' }}>
            {v}
            <button type="button" onClick={() => remove(v)} style={{ cursor: 'pointer', border: 'none', background: 'transparent', color: C.label, fontSize: 12, lineHeight: 1, padding: 0 }} aria-label={`Remove ${v}`}>×</button>
          </span>
        ))}
        {!adding ? (
          <button type="button" onClick={() => setAdding(true)} style={{ fontSize: 11, fontWeight: 700, color: C.accent, background: 'transparent', border: `1px dashed ${C.accent}`, borderRadius: 999, padding: '4px 10px', cursor: 'pointer' }}>+ Add</button>
        ) : (
          <span style={{ display: 'flex', gap: 6 }}>
            <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setAdding(false); setDraft(''); } }}
              style={{ fontSize: 11, color: C.body, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '5px 8px', width: 160 }} />
            <button type="button" onClick={commit} style={{ cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#fff', background: C.accent, border: 'none', borderRadius: 8, padding: '5px 10px' }}>Add</button>
          </span>
        )}
      </div>
    </div>
  );
}

function QuestionField({ q, draft, onChange }: { q: AttestationQuestion; draft: AnswerDraft | undefined; onChange: (d: AnswerDraft) => void }) {
  const value = draft?.value;
  const selectedSingle = typeof value === 'string' ? value : '';
  const selectedMulti = Array.isArray(value) ? value : [];
  const selectedOption = q.options.find((o) => o.value === selectedSingle);

  if (q.kind === 'single') {
    return (
      <div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {q.options.map((o) => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.body, cursor: 'pointer' }}>
              <input type="radio" name={q.id} checked={selectedSingle === o.value} onChange={() => onChange({ value: o.value, extra: null })} />
              {o.label}
            </label>
          ))}
        </div>
        {selectedOption?.extra && (
          <input
            value={draft?.extra || ''}
            onChange={(e) => onChange({ value: selectedSingle, extra: e.target.value })}
            placeholder={selectedOption.extra.placeholder || selectedOption.extra.label}
            type={selectedOption.extra.type === 'date' ? 'date' : 'text'}
            style={{ marginTop: 6, fontSize: 12, color: C.body, background: C.soft, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 8px', width: '100%', maxWidth: 260 }}
          />
        )}
        {q.extra && selectedSingle && selectedSingle !== 'no' && selectedSingle !== C_DONT_KNOW && (
          <input
            value={draft?.extra || ''}
            onChange={(e) => onChange({ value: selectedSingle, extra: e.target.value })}
            placeholder={q.extra.placeholder || q.extra.label}
            style={{ marginTop: 6, fontSize: 12, color: C.body, background: C.soft, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 8px', width: '100%', maxWidth: 320 }}
          />
        )}
      </div>
    );
  }

  // multi — "Don't know" is exclusive with any other selection (mirrors the backend's rule).
  const toggle = (v: string) => {
    if (v === C_DONT_KNOW) { onChange({ value: selectedMulti.includes(v) ? [] : [v] }); return; }
    const withoutDontKnow = selectedMulti.filter((x) => x !== C_DONT_KNOW);
    const next = withoutDontKnow.includes(v) ? withoutDontKnow.filter((x) => x !== v) : [...withoutDontKnow, v];
    onChange({ value: next });
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {q.options.map((o) => (
        <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.body, cursor: 'pointer' }}>
          <input type="checkbox" checked={selectedMulti.includes(o.value)} onChange={() => toggle(o.value)} />
          {o.label}
        </label>
      ))}
    </div>
  );
}

const C_DONT_KNOW = 'dont_know';

export default function OwnerAttestation() {
  const att = useAttestation();
  const live = useFrameworkData();

  const [tab, setTab] = useState<Tab>('gaps');
  const [ownerFilter, setOwnerFilter] = useState<'all' | string>('all');
  const [selectedEucId, setSelectedEucId] = useState<string>('');
  const [draftAnswers, setDraftAnswers] = useState<Record<string, AnswerDraft>>({});
  const [saving, setSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState<{ question_id: string; error: string }[]>([]);
  const [saveMsg, setSaveMsg] = useState('');

  const [exportOwner, setExportOwner] = useState('');
  const [importMsg, setImportMsg] = useState('');
  const [pasteText, setPasteText] = useState('');

  const [configDraft, setConfigDraft] = useState<{ submission_list: string[]; system_list: string[] } | null>(null);
  const [configMsg, setConfigMsg] = useState('');
  const config = configDraft || att.config;

  const gapsRows = useMemo(() => {
    const rows = att.coverage?.by_euc || [];
    return ownerFilter === 'all' ? rows : rows.filter((r) => (r.owner || 'Unassigned') === ownerFilter);
  }, [att.coverage, ownerFilter]);
  const sortedGaps = useMemo(() => [...gapsRows].sort((a, b) => a.answered - b.answered), [gapsRows]);

  const selectedEuc = live.records.find((r) => r.euc_id === selectedEucId);
  const existingAnswers = att.answers[selectedEucId] || {};

  const openForAnswering = (row: CoverageEucRow) => {
    setSelectedEucId(row.euc_id);
    setDraftAnswers({});
    setSaveErrors([]); setSaveMsg('');
    setTab('answer');
  };

  const questionValue = (qid: string): AnswerDraft | undefined => {
    if (draftAnswers[qid]) return draftAnswers[qid];
    const existing = existingAnswers[qid];
    if (!existing) return undefined;
    return { value: existing.value, extra: existing.extra ?? null };
  };

  const submit = async () => {
    const payload: Record<string, { value: string | string[]; extra?: string | null }> = {};
    for (const [qid, d] of Object.entries(draftAnswers)) {
      if (!d) continue;
      const empty = Array.isArray(d.value) ? d.value.length === 0 : !d.value;
      if (empty) continue;
      payload[qid] = { value: d.value, extra: d.extra ?? null };
    }
    if (!Object.keys(payload).length) { setSaveMsg('Nothing changed to save.'); return; }
    setSaving(true);
    const res = await att.saveAnswers(selectedEucId, payload);
    setSaving(false);
    if (!res.ok) { setSaveMsg(res.error || 'Save failed'); return; }
    setSaveErrors(res.errors || []);
    setDraftAnswers({});
    await att.loadCoverage();
    setSaveMsg(res.errors && res.errors.length ? `Saved with ${res.errors.length} rejected.` : 'Saved.');
  };

  const doExport = async () => {
    if (!exportOwner) return;
    const res = await att.exportPack(exportOwner);
    setImportMsg(res.ok ? '' : (res.error || 'Export failed'));
  };

  const doImportFile = async (file: File) => {
    setImportMsg('Importing…');
    const res = await att.importFile(file);
    if (!res.ok) { setImportMsg(res.error || 'Import failed'); return; }
    const r = res.result!;
    setImportMsg(`${r.eucs_imported} EUC(s) updated from ${r.rows_total} row(s)${r.error_count ? `, ${r.error_count} rejected` : ''}.`);
  };

  const doImportPaste = async () => {
    const lines = pasteText.split('\n').map((l) => l.trim()).filter(Boolean);
    const rows = lines.map((l) => {
      const [euc_id, question_id, response, notes] = l.split('\t');
      return { euc_id: (euc_id || '').trim(), question_id: (question_id || '').trim(), response: (response || '').trim(), notes: (notes || '').trim() };
    }).filter((r) => r.euc_id && r.question_id && r.response);
    if (!rows.length) { setImportMsg('Paste tab-separated rows: EUC ID, Question ID, Response, Notes (optional).'); return; }
    setImportMsg('Importing…');
    const res = await att.importRows(rows);
    if (!res.ok) { setImportMsg(res.error || 'Import failed'); return; }
    const r = res.result!;
    setImportMsg(`${r.eucs_imported} EUC(s) updated from ${r.rows_total} row(s)${r.error_count ? `, ${r.error_count} rejected` : ''}.`);
    setPasteText('');
  };

  const saveConfigNow = async () => {
    if (!configDraft) return;
    const res = await att.saveConfig(configDraft);
    setConfigMsg(res.ok ? 'Saved.' : (res.error || 'Save failed'));
    if (res.ok) setConfigDraft(null);
  };

  if (att.questionsLoaded && !live.loading && live.records.length === 0) {
    return (
      <div style={{ fontFamily: font.ui, color: C.body, maxWidth: 560, margin: '48px auto', textAlign: 'center', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: '40px 32px' }}>
        <div style={{ fontFamily: font.ui, fontSize: 11, letterSpacing: '0.12em', color: C.label, textTransform: 'uppercase' }}>Owner Attestation</div>
        <h2 style={{ margin: '10px 0 6px', fontSize: 18, fontWeight: 800, color: C.ink }}>No inventory loaded yet</h2>
        <p style={{ fontSize: 13.5, color: C.subtle, margin: 0 }}>Load an inventory from the Framework Mapping tab first — there's nothing to attest until EUCs exist.</p>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: font.ui, color: C.body }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: font.ui, fontSize: 11, letterSpacing: '0.12em', color: C.label, textTransform: 'uppercase' }}>Owner Attestation</div>
        <h1 style={{ margin: '4px 0 8px', fontSize: 18, fontWeight: 800, color: C.ink, letterSpacing: '-0.02em' }}>
          Capture what the file can't tell us.
        </h1>
        {att.coverage && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 16px', maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
              <span style={{ color: C.subtle }}>Answer coverage</span>
              <b style={{ color: C.ink }}>{att.coverage.pct}% <span style={{ fontWeight: 500, color: C.subtle }}>({att.coverage.answered_total} of {att.coverage.possible_total} across {att.coverage.total_eucs})</span></b>
            </div>
            <ProgressBar pct={att.coverage.pct} />
          </div>
        )}
      </div>

      <div style={{ display: 'inline-flex', gap: 4, background: '#eceef7', padding: 4, borderRadius: 12, marginBottom: 20 }}>
        {(['gaps', 'answer', 'distribute', 'config'] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 14px', border: 'none', borderRadius: 9, fontFamily: font.ui, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            background: tab === t ? C.surface : 'transparent', color: tab === t ? C.ink : C.label, boxShadow: tab === t ? '0 1px 3px #0d0b2b18' : 'none', textTransform: 'capitalize',
          }}>{t === 'gaps' ? 'Gaps' : t === 'answer' ? 'Answer' : t === 'distribute' ? 'Export / Import' : 'Config'}</button>
        ))}
      </div>

      {tab === 'gaps' && (
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} style={{ fontFamily: mono, fontSize: 12, color: C.ink, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 8px' }}>
              <option value="all">All owners</option>
              {(att.coverage?.by_owner || []).map((o) => <option key={o.owner} value={o.owner}>{o.owner} ({o.pct}%)</option>)}
            </select>
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 0.8fr 1.6fr', gap: 8, padding: '9px 14px', background: C.soft, borderBottom: `1px solid ${C.border}`, fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', color: C.label, textTransform: 'uppercase' }}>
              <span>EUC</span><span>Owner</span><span>Department</span><span>Answered</span><span>Outstanding</span>
            </div>
            {sortedGaps.length === 0 ? (
              <div style={{ padding: '24px 14px', textAlign: 'center', fontSize: 12.5, color: C.subtle }}>No EUCs match this filter.</div>
            ) : sortedGaps.map((r) => (
              <button key={r.euc_id} onClick={() => openForAnswering(r)} style={{
                display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 0.8fr 1.6fr', gap: 8, padding: '10px 14px', width: '100%', textAlign: 'left',
                border: 'none', borderBottom: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
              }}>
                <div>
                  <div style={{ fontFamily: mono, fontSize: 12, fontWeight: 700, color: C.ink }}>{r.file}</div>
                  <div style={{ fontSize: 11, color: C.subtle }}>{r.euc_id}</div>
                </div>
                <div style={{ fontSize: 12, color: C.body }}>{r.owner || '—'}</div>
                <div style={{ fontSize: 12, color: C.body }}>{r.dept}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: r.answered === r.total ? '#166534' : C.ink }}>{r.answered}/{r.total}</div>
                <div style={{ fontSize: 11, color: C.subtle }}>{r.missing.length ? r.missing.join(', ') : 'Complete'}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === 'answer' && (
        <div>
          <select value={selectedEucId} onChange={(e) => { setSelectedEucId(e.target.value); setDraftAnswers({}); setSaveErrors([]); setSaveMsg(''); }} style={{
            fontFamily: mono, fontSize: 12, color: C.ink, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 10px', marginBottom: 16, minWidth: 280,
          }}>
            <option value="">Select an EUC…</option>
            {live.records.map((r) => <option key={r.euc_id} value={r.euc_id}>{r.file} ({r.euc_id})</option>)}
          </select>

          {selectedEuc && (
            <div>
              <div style={{ fontSize: 12, color: C.subtle, marginBottom: 14 }}>{selectedEuc.dept} {selectedEuc.owner ? `· ${selectedEuc.owner}` : ''}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {att.required.map((qid) => {
                  const q = att.questions.find((x) => x.id === qid);
                  if (!q) return null;
                  const subQ = q.hasSubQuestion ? att.questions.find((x) => x.id === q.hasSubQuestion) : null;
                  const err = saveErrors.find((e) => e.question_id === qid);
                  return (
                    <div key={qid} style={{ background: C.surface, border: `1px solid ${err ? '#fecaca' : C.border}`, borderRadius: 14, padding: 14 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: C.ink, marginBottom: 8 }}>{qid.replace('Q', '')}. {q.prompt}</div>
                      <QuestionField q={q} draft={questionValue(qid)} onChange={(d) => setDraftAnswers((p) => ({ ...p, [qid]: d }))} />
                      {subQ && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: C.subtle, marginBottom: 6 }}>{subQ.prompt}</div>
                          <QuestionField q={subQ} draft={questionValue(subQ.id)} onChange={(d) => setDraftAnswers((p) => ({ ...p, [subQ.id]: d }))} />
                        </div>
                      )}
                      {err && <div style={{ marginTop: 8, fontSize: 11, color: '#991b1b' }}>{err.error}</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                <button onClick={submit} disabled={saving} style={{
                  cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1, fontSize: 12.5, fontWeight: 700, color: '#fff', background: C.accent, border: 'none', borderRadius: 10, padding: '9px 18px',
                }}>
                  {saving ? 'Saving…' : 'Save answers'}
                </button>
                {saveMsg && <span style={{ fontSize: 12, color: C.subtle }}>{saveMsg}</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'distribute' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 640 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.ink, marginBottom: 4 }}>Export a per-owner pack</div>
            <p style={{ fontSize: 12, color: C.subtle, margin: '0 0 12px' }}>
              An .xlsx covering every EUC that owner has, all 11 questions, already-answered ones pre-filled. Drop it into email, an MS Form, Teams — whatever channel this engagement uses.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={exportOwner} onChange={(e) => setExportOwner(e.target.value)} style={{ flex: 1, fontFamily: mono, fontSize: 12, color: C.ink, background: C.soft, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 10px' }}>
                <option value="">Select an owner…</option>
                {(att.coverage?.by_owner || []).map((o) => <option key={o.owner} value={o.owner}>{o.owner} — {o.euc_count} EUC{o.euc_count === 1 ? '' : 's'}, {o.pct}% answered</option>)}
              </select>
              <button onClick={doExport} disabled={!exportOwner} style={{ cursor: exportOwner ? 'pointer' : 'default', opacity: exportOwner ? 1 : 0.5, fontSize: 12, fontWeight: 700, color: '#fff', background: C.accent, border: 'none', borderRadius: 9, padding: '8px 16px' }}>
                Export
              </button>
            </div>
          </div>

          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.ink, marginBottom: 4 }}>Import responses</div>
            <p style={{ fontSize: 12, color: C.subtle, margin: '0 0 12px' }}>
              Upload a filled-in export pack (or any spreadsheet with EUC ID / Question ID / Response columns), or paste rows directly — one EUC ID, Question ID, Response, Notes per line, tab-separated.
            </p>
            <input type="file" accept=".xlsx" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) doImportFile(f); }} style={{ fontSize: 12, marginBottom: 12 }} />
            <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={5} placeholder={'EUC-0012\tQ1\tYes\nEUC-0012\tQ2\tDaily'} style={{
              width: '100%', fontFamily: mono, fontSize: 11, color: C.body, background: C.soft, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, resize: 'vertical', marginBottom: 8,
            }} />
            <button onClick={doImportPaste} disabled={!pasteText.trim()} style={{ cursor: pasteText.trim() ? 'pointer' : 'default', opacity: pasteText.trim() ? 1 : 0.5, fontSize: 12, fontWeight: 700, color: '#fff', background: C.deep, border: 'none', borderRadius: 9, padding: '7px 14px' }}>
              Import pasted rows
            </button>
            {importMsg && <div style={{ marginTop: 10, fontSize: 12, color: C.subtle }}>{importMsg}</div>}
          </div>
        </div>
      )}

      {tab === 'config' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 560 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
            <p style={{ fontSize: 12, color: C.subtle, margin: '0 0 16px' }}>
              These two lists are engagement-specific — Q5's named regulatory/external submissions, and Q10's "another system already does this" catalog. Never hardcoded to one client.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <ListEditor label="Q5 — Named submissions" items={config.submission_list} onChange={(v) => setConfigDraft({ ...config, submission_list: v })} />
              <ListEditor label="Q10 — Known systems" items={config.system_list} onChange={(v) => setConfigDraft({ ...config, system_list: v })} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18 }}>
              <button onClick={saveConfigNow} disabled={!configDraft} style={{ cursor: configDraft ? 'pointer' : 'default', opacity: configDraft ? 1 : 0.5, fontSize: 12, fontWeight: 700, color: '#fff', background: C.accent, border: 'none', borderRadius: 9, padding: '8px 16px' }}>
                Save
              </button>
              {configMsg && <span style={{ fontSize: 12, color: C.subtle }}>{configMsg}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
